/* global axios */

import ApiClient from './ApiClient';
import camelcaseKeys from 'camelcase-keys';

export const camelizeResponse = response => ({
  ...response,
  data: camelcaseKeys(response.data, {
    deep: true,
    stopPaths: ['meta.stage_counts'],
  }),
});

const withCamelizedResponse = request => request.then(camelizeResponse);

class ClinicalCrmAPI extends ApiClient {
  constructor() {
    super('crm', { accountScoped: true });
  }

  getOpportunities(params = {}) {
    return withCamelizedResponse(
      axios.get(`${this.url}/opportunities`, { params })
    );
  }

  getOpportunity(id) {
    return withCamelizedResponse(axios.get(`${this.url}/opportunities/${id}`));
  }

  createOpportunity(opportunity) {
    return withCamelizedResponse(
      axios.post(`${this.url}/opportunities`, { opportunity })
    );
  }

  updateOpportunity(id, opportunity) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/opportunities/${id}`, { opportunity })
    );
  }

  updateStage(id, stage, source = 'manual') {
    return withCamelizedResponse(
      axios.patch(`${this.url}/opportunities/${id}/stage`, {
        stage,
        source,
      })
    );
  }

  markNotLead(id, reason, source = 'manual') {
    return withCamelizedResponse(
      axios.patch(`${this.url}/opportunities/${id}/not_lead`, {
        reason,
        source,
      })
    );
  }

  restoreOpportunity(id, source = 'manual') {
    return withCamelizedResponse(
      axios.patch(`${this.url}/opportunities/${id}/restore`, { source })
    );
  }

  getStageColors() {
    return withCamelizedResponse(axios.get(`${this.url}/stage_colors`));
  }

  updateStageColors(stageColors, expectedVersion) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/stage_colors`, {
        stage_colors: stageColors,
        expected_version: expectedVersion,
      })
    );
  }

  getMediaAssets(params = {}) {
    return withCamelizedResponse(
      axios.get(`${this.url}/media_assets`, { params })
    );
  }

  createMediaAsset(mediaAsset) {
    return withCamelizedResponse(
      axios.post(`${this.url}/media_assets`, { media_asset: mediaAsset })
    );
  }

  updateMediaAsset(id, mediaAsset) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/media_assets/${id}`, { media_asset: mediaAsset })
    );
  }

  archiveMediaAsset(id) {
    return axios.delete(`${this.url}/media_assets/${id}`);
  }

  getQuickReplies(params = {}) {
    return withCamelizedResponse(
      axios.get(`${this.url}/quick_replies`, { params })
    );
  }

  createQuickReply(quickReply) {
    return withCamelizedResponse(
      axios.post(`${this.url}/quick_replies`, { quick_reply: quickReply })
    );
  }

  updateQuickReply(id, quickReply) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/quick_replies/${id}`, {
        quick_reply: quickReply,
      })
    );
  }

  archiveQuickReply(id) {
    return axios.delete(`${this.url}/quick_replies/${id}`);
  }

  getProcedures(params = {}) {
    return withCamelizedResponse(
      axios.get(`${this.url}/procedures`, { params })
    );
  }

  createProcedure(procedure) {
    return withCamelizedResponse(
      axios.post(`${this.url}/procedures`, { procedure })
    );
  }

  updateProcedure(id, procedure) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/procedures/${id}`, { procedure })
    );
  }

  deleteProcedure(id) {
    return axios.delete(`${this.url}/procedures/${id}`);
  }

  getAnalytics(params = {}) {
    return withCamelizedResponse(
      axios.get(`${this.url}/analytics`, { params })
    );
  }

  getExportPermissions() {
    return withCamelizedResponse(axios.get(`${this.url}/export_permissions`));
  }

  updateExportPermission(userId, exportPermission) {
    return withCamelizedResponse(
      axios.patch(`${this.url}/export_permissions/${userId}`, {
        export_permission: exportPermission,
      })
    );
  }

  getExports() {
    return withCamelizedResponse(axios.get(`${this.url}/exports`));
  }

  createExport(crmExport) {
    return withCamelizedResponse(
      axios.post(`${this.url}/exports`, { crm_export: crmExport })
    );
  }

  downloadExport(id) {
    return axios.get(`${this.url}/exports/${id}/download`, {
      responseType: 'blob',
    });
  }
}

export default new ClinicalCrmAPI();
